/**
 * AeroGestor offline outbox.
 *
 * Queues mutating API requests when the device is offline and replays them
 * when connectivity returns. Backed by localStorage for simplicity.
 */
import { api } from "@/lib/api";

const KEY = "aerogestor:outbox";
let listeners = [];

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function writeQueue(q) {
  localStorage.setItem(KEY, JSON.stringify(q));
  for (const cb of listeners) {
    try { cb(q); } catch { /* noop */ }
  }
}

export function getQueue() {
  return readQueue();
}

export function subscribe(cb) {
  listeners.push(cb);
  cb(readQueue());
  return () => {
    listeners = listeners.filter((x) => x !== cb);
  };
}

function enqueue(item) {
  const q = readQueue();
  q.push({
    id: (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now() + Math.random()),
    ts: Date.now(),
    ...item,
  });
  writeQueue(q);
}

function isNetworkError(err) {
  if (!navigator.onLine) return true;
  if (!err) return false;
  if (err.code === "ERR_NETWORK") return true;
  if (err.message && /network|offline|failed to fetch/i.test(err.message)) return true;
  return false;
}

/**
 * Perform a mutating request. If offline / network error, queue it locally.
 * Returns either the actual response data or { queued: true, label }.
 */
export async function offlineRequest({ method, url, data, label }) {
  if (!navigator.onLine) {
    enqueue({ method, url, data, label });
    return { queued: true, label };
  }
  try {
    const r = await api.request({ method, url, data });
    return r.data;
  } catch (e) {
    if (isNetworkError(e)) {
      enqueue({ method, url, data, label });
      return { queued: true, label };
    }
    throw e;
  }
}

/** Replay queued mutations sequentially. Stops on first network failure. */
export async function flushQueue() {
  if (!navigator.onLine) return { sent: 0, failed: 0, remaining: readQueue().length };
  const q = readQueue();
  if (!q.length) return { sent: 0, failed: 0, remaining: 0 };

  let sent = 0;
  let failed = 0;
  const remaining = [];

  for (let i = 0; i < q.length; i++) {
    const item = q[i];
    try {
      await api.request({ method: item.method, url: item.url, data: item.data });
      sent++;
    } catch (e) {
      if (isNetworkError(e)) {
        // Keep this and the rest in order to preserve sequence
        remaining.push(...q.slice(i));
        break;
      }
      // Server rejected the request — drop it (it's invalid)
      failed++;
    }
  }
  writeQueue(remaining);
  return { sent, failed, remaining: remaining.length };
}

export function startOfflineSync() {
  window.addEventListener("online", () => {
    flushQueue();
  });
  // Attempt initial flush on app load (in case queue exists from previous session)
  if (navigator.onLine) {
    setTimeout(() => flushQueue(), 1500);
  }
}
